// Cross-feature KPI card — a stat tile with accent border, watermark icon, optional
// period-over-period DeltaBadge, optional value tooltip, and an optional sparkline.
//
// Was copy-pasted (structurally identical) into google-ads/shared/components.js and
// google-analytics/KpiCards.js; this is the single source. The GA4 copy had added
// responsive variants (p-4 sm:p-6, text-2xl sm:text-3xl, responsive watermark icon,
// break-words) — adopted here as the baseline since they're a strict superset: at
// >=640px the classes resolve to exactly the old Ads desktop styling, and below 640px
// the card simply gains the mobile-friendlier treatment. The icon set is the UNION of
// both features' icons (the shared keys — chart/dollar/conversions/percent — were
// byte-identical in both).

import React from 'react';
import { Area, ComposedChart, Line, ResponsiveContainer } from 'recharts';
import InfoTip from './InfoTip';
import { Skeleton } from './Skeleton';
import { DeltaBadge } from './DeltaBadge';
import { KPI_ACCENT_BORDERS, KPI_ICON_COLORS, KPI_ICONS } from './kpiTokens';

// The three token maps live in ./kpiTokens (a dependency-free module) and are
// re-exported here so a feature that needs this card's ANATOMY without its
// behaviour can reuse the palette rather than mirror it — and can do so WITHOUT
// pulling in this file's recharts / InfoTip / DeltaBadge deps. Search Console's
// `MetricTile` and the conversation MoneyStrip are both that case: they borrow the
// accent + watermark tokens but keep their own change treatment and layout.
export { KPI_ACCENT_BORDERS, KPI_ICON_COLORS, KPI_ICONS };

export const KpiCard = React.memo(({ title, value, subtitle, changePct, direction = 'higher_good', icon = 'chart', tooltip, valueTooltip, loading: isLoading = false, variant = 'primary', sparkData }) => (
  <div className={`relative bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 border-t-[3px] ${KPI_ACCENT_BORDERS[variant] ?? KPI_ACCENT_BORDERS.primary} p-4 sm:p-6 min-h-[120px] sm:min-h-[140px] flex flex-col transition-shadow overflow-hidden`}>
    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
      <div className={`absolute -top-4 -right-4 sm:-top-6 sm:-right-6 ${KPI_ICON_COLORS[variant] ?? KPI_ICON_COLORS.primary}`}>
        <svg className="w-16 h-16 sm:w-24 sm:h-24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
          {KPI_ICONS[icon] ?? KPI_ICONS.chart}
        </svg>
      </div>
    </div>
    <div className="flex items-center flex-wrap gap-1.5 relative z-20">
      <div className="text-[12px] sm:text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{title}</div>
      {tooltip && <InfoTip text={tooltip} />}
      <DeltaBadge value={changePct} direction={direction} />
    </div>
    {isLoading ? (
      <Skeleton className="mt-2 h-8 sm:h-9 w-28 relative z-10" />
    ) : valueTooltip ? (
      <InfoTip text={valueTooltip}>
        <div className="mt-2 text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-gray-100 relative z-10 break-words">{value}</div>
      </InfoTip>
    ) : (
      <div className="mt-2 text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-gray-100 relative z-10 break-words">{value}</div>
    )}
    {subtitle && <div className="text-xs sm:text-sm mt-1.5 text-gray-500 dark:text-gray-400 relative z-10">{subtitle}</div>}
    {sparkData?.length > 1 && !isLoading && (
      <div className="mt-auto pt-2 -mx-4 -mb-4 sm:-mx-6 sm:-mb-6 relative z-10 rounded-b-xl overflow-hidden">
        <ResponsiveContainer width="100%" height={36}>
          <ComposedChart data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`sparkKpi_${title.replace(/\s+/g, '_')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="v" fill={`url(#sparkKpi_${title.replace(/\s+/g, '_')})`} stroke="none" isAnimationActive={false} />
            <Line type="monotone" dataKey="v" stroke="#94a3b8" strokeWidth={1.2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
));

KpiCard.displayName = 'KpiCard';

export default KpiCard;
